import { Dropdown } from '@h2oai/ui-kit';
import { Chart, ChartDataset } from 'chart.js';
import { useEffect, useRef, useState } from 'react';

import { lineChartOptions } from '../../../common/utils';
import LineChart from '../../LineChart/LineChart';
import { chartWrapperStyle, monthSelectDropdownStyles, PageStyle, statBarStyle } from '../AIUnitHourly.styles';
import { useAIUnitHourlyComponentData } from '../hooks';
import { getAIUnitHourlyMonthDropdownOptions, getMonthRange } from '../utils';

interface IComponentSummaryProps {
  envStartDate: string;
  envEndDate: string;
}

export default function ComponentSummary({ envStartDate, envEndDate }: IComponentSummaryProps) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const [visibleTotal, setVisibleTotal] = useState<number>(0);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (!envStartDate || !envEndDate || envStartDate === '0000-00-00' || envEndDate === '0000-00-00') {
      setSelectedPeriodKey('entire-period');
      setStartDate(null);
      setEndDate(null);
    } else {
      setSelectedPeriodKey('contract-period');
      const environmentStartDate = new Date(envStartDate);
      const environmentEndDate = new Date(envEndDate);

      const firstDate = new Date(
        Date.UTC(environmentStartDate.getFullYear(), environmentStartDate.getMonth(), environmentStartDate.getDate())
      );
      const lastDate = new Date(
        Date.UTC(environmentEndDate.getFullYear(), environmentEndDate.getMonth(), environmentEndDate.getDate())
      );
      setStartDate(firstDate);
      setEndDate(lastDate);
    }

    setIsInitialized(true);
  }, [envStartDate, envEndDate]);

  const {
    componentData,
    firstDate,
    lastDate,
    loading: dataLoading,
    error,
    isEmpty,
    threshold,
  } = useAIUnitHourlyComponentData(isInitialized ? startDate : null, isInitialized ? endDate : null);

  const loading = dataLoading || !isInitialized;

  const updateVisibleTotal = (chart: Chart<'line'>) => {
    const datasets = chart.data.datasets;
    const visibleData = datasets
      .filter((_, idx) => !chart.getDatasetMeta(idx).hidden)
      .flatMap((ds: ChartDataset<'line'>) => ds.data);

    const sum = visibleData.filter((val): val is number => typeof val === 'number').reduce((acc, val) => acc + val, 0);
    setVisibleTotal(sum);
  };

  const totalPlugin = {
    id: 'totalPlugin',
    afterEvent(chart: Chart<'line'>, args: any) {
      const e = args.event;
      if (e.type === 'click') {
        updateVisibleTotal(chart);
      }
    },
    afterDatasetsDraw(chart: Chart<'line'>) {
      updateVisibleTotal(chart);
    },
  };

  const getUsagePercentage = (): string => {
    if (threshold) {
      return `${((visibleTotal / threshold) * 100).toFixed(2)}%`;
    } else {
      return 'N/A';
    }
  };

  return (
    <>
      <div style={PageStyle}>
        <div style={statBarStyle}>
          <Dropdown
            options={getAIUnitHourlyMonthDropdownOptions(firstDate || '', lastDate || '')}
            selectedKey={selectedPeriodKey}
            styles={monthSelectDropdownStyles}
            disabled={isEmpty || loading}
            onChange={(_, option) => {
              if (!option) return;
              setSelectedPeriodKey(option.key as string);

              if (option.key === 'entire-period') {
                setStartDate(null);
                setEndDate(null);
              } else {
                const { firstDate, lastDate } = getMonthRange(option.key as string);
                setStartDate(new Date(firstDate));
                setEndDate(new Date(lastDate));
              }
            }}
          />
        </div>
        <div style={chartWrapperStyle}>
          <LineChart
            data={componentData}
            options={lineChartOptions(
              `AI Unit Usage - ${getUsagePercentage()}`,
              componentData && componentData.labels?.length ? componentData.labels?.length : 0,
              16
            )}
            ref={(ref) => {
              if (ref) {
                chartRef.current = ref;
              }
            }}
            plugins={[totalPlugin]}
            loading={loading}
            error={error ? true : false}
          />
        </div>
      </div>
    </>
  );
}
